import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Theme, ChatSession, ChatMessage } from '@/types'
import { STORAGE_KEYS } from '@/consts'

// Auth Store
interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  setAuth: (auth: { user: User; accessToken: string; refreshToken: string }) => void
  updateUser: (user: Partial<User>) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      setAuth: ({ user, accessToken, refreshToken }) =>
        set({ user, accessToken, refreshToken, isAuthenticated: true }),
      updateUser: (userUpdate) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userUpdate } : null,
        })),
      logout: () =>
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),
    }),
    {
      name: STORAGE_KEYS.USER,
    }
  )
)

// Theme Store
interface ThemeState {
  theme: Theme['mode']
  setTheme: (theme: Theme['mode']) => void
  toggleTheme: () => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      setTheme: (theme) => set({ theme }),
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === 'light' ? 'dark' : 'light',
        })),
    }),
    {
      name: STORAGE_KEYS.THEME,
    }
  )
)

// Chat Store
interface ChatState {
  sessions: ChatSession[]
  currentSessionId: string | null
  isStreaming: boolean
  addSession: (session: ChatSession) => void
  updateSession: (id: string, updates: Partial<ChatSession>) => void
  deleteSession: (id: string) => void
  setCurrentSession: (id: string | null) => void
  addMessage: (sessionId: string, message: ChatMessage) => void
  updateMessage: (sessionId: string, messageId: string, updates: Partial<ChatMessage>) => void
  setIsStreaming: (isStreaming: boolean) => void
  clearCurrentSession: () => void
}

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      sessions: [],
      currentSessionId: null,
      isStreaming: false,
      addSession: (session) =>
        set((state) => ({
          sessions: [session, ...state.sessions],
          currentSessionId: session.id,
        })),
      updateSession: (id, updates) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          ),
        })),
      deleteSession: (id) =>
        set((state) => ({
          sessions: state.sessions.filter((s) => s.id !== id),
          currentSessionId:
            state.currentSessionId === id ? null : state.currentSessionId,
        })),
      setCurrentSession: (id) => set({ currentSessionId: id }),
      addMessage: (sessionId, message) =>
        set((state) => {
          // Prevent duplicate messages with same ID in the same session
          const session = state.sessions.find((s) => s.id === sessionId)
          if (session?.messages.some((m) => m.id === message.id)) {
            return state
          }
          return {
            sessions: state.sessions.map((s) =>
              s.id === sessionId
                ? { ...s, messages: [...s.messages, message], updated_at: new Date().toISOString() }
                : s
            ),
          }
        }),
      updateMessage: (sessionId, messageId, updates) =>
        set((state) => ({
          sessions: state.sessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  messages: s.messages.map((m) =>
                    m.id === messageId ? { ...m, ...updates } : m
                  ),
                }
              : s
          ),
        })),
      setIsStreaming: (isStreaming) => set({ isStreaming }),
      clearCurrentSession: () => set({ currentSessionId: null }),
    }),
    {
      name: 'chat_sessions',
    }
  )
)

// UI Store
interface UIState {
  sidebarCollapsed: boolean
  sidebarMini: boolean
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleSidebarMini: () => void
  setSidebarMini: (mini: boolean) => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      sidebarMini: false,
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebarMini: () =>
        set((state) => ({ sidebarMini: !state.sidebarMini })),
      setSidebarMini: (mini) => set({ sidebarMini: mini }),
    }),
    {
      name: STORAGE_KEYS.SIDEBAR_MINI,
    }
  )
)
